import { useCallback, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import clsx from 'clsx';
import { z } from 'zod';

import { Bulletin, useBulletin } from '@autopass-web/feature-bulletin';
import {
  EventInView,
  EventScope,
  useClickEvent,
  useInViewedEvent,
} from '@autopass-web/mochi-analytics/event';
import { MochiLottie } from '@autopass-web/mochi-component';
import { MochiSparkles } from '@autopass-web/mochi-component';

import lottieMochiCheckJSON from '../assets/lottie-mochi-check.json';
import { MainLayout } from '../components/main-layout';
import { axiosInstance } from '../services/client';
import { useInvoice } from '../services/use-invoice';
import { useSubmitRecords } from '../stores/use-submit-records';
import { useSubmitRecordsV2 } from '../stores/use-submit-records-v2';
import style from './success.module.scss';

import type { ReactNode } from 'react';

const SUCCESS_BOARD_CODE = 'chain_store_success_page';

const SuccessBulletin = () => {
  const bulletin = useBulletin({
    clientInstance: axiosInstance,
    boardCode: SUCCESS_BOARD_CODE,
    attributesSchema: z.object({
      image_url: z.string(),
    }),
  });

  const handleClickEvent = useClickEvent('廣告曝光');
  const handleInViewEvent = useInViewedEvent('廣告曝光');

  const handleBulletinInViewEvent = useCallback(
    (bulletin) => {
      handleInViewEvent(
        {
          廣告id: bulletin.id,
          版位id: SUCCESS_BOARD_CODE,
        },
        { key: bulletin.id },
      );
    },
    [handleInViewEvent],
  );

  const availBoards = bulletin.data ?? [];

  return (
    <Bulletin
      {...bulletin}
      data={availBoards}
      handleBoardClick={(bulletin) => {
        handleClickEvent({
          廣告id: bulletin.id,
          版位id: SUCCESS_BOARD_CODE,
        });
      }}
      handleBoardInView={handleBulletinInViewEvent}
    />
  );
};

const Layout = ({ children }: { children?: ReactNode }) => {
  return (
    <MainLayout
      className={clsx('bg-[#0075FF]', style.success)}
      logoColor="white"
    >
      <MochiSparkles />
      <div className="flex flex-col justify-between items-stretch h-full px-5">
        <div className="flex flex-1 justify-center flex-col">
          <div className="flex items-center justify-center pt-[75px] pb-[50px] rounded-[10px] bg-white px-8 relative">
            <div className=" bg-white rounded-full w-[90px] h-[90px] absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 shadow-[0_4px_16px_rgba(0,0,0,0.12)]">
              <MochiLottie
                style={{ width: 90, height: 90 }}
                animationData={lottieMochiCheckJSON}
                loop={false}
              />
            </div>
            <div className="flex flex-col items-center ">{children}</div>
          </div>
        </div>
        <div className="pb-2">
          <SuccessBulletin />
        </div>
      </div>
    </MainLayout>
  );
};

export function Success() {
  const { invoiceId } = useParams();

  const isInvoiceIdValid = typeof invoiceId === 'string';

  const { invoice, isSuccess } = useInvoice(
    { invoiceId },
    {
      enabled: isInvoiceIdValid,
    },
  );

  const { cleanRecords } = useSubmitRecords(invoice.id);
  const { cleanup } = useSubmitRecordsV2(invoice.id);

  useEffect(() => {
    // if locate in success page, means paid and delete the invoice records
    cleanRecords();
    cleanup();
  }, [cleanRecords, cleanup]);

  return (
    <EventScope.Page page="交易成功頁">
      <EventInView
        isReady={isSuccess}
        target="交易成功"
        props={{ 連鎖店名稱: invoice.chain_store_name }}
      >
        <Layout>
          <span>{invoice.chain_store_name}</span>
          <span className="mt-5 text-[20px] font-medium">付款完成</span>
          <span className="text-[#00D7B2] text-[50px]">
            {+invoice.amount} 元
          </span>
        </Layout>
      </EventInView>
    </EventScope.Page>
  );
}
